import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import services from '../../services'
import Janela from '../componentes/Janela'
import { useNotification } from '../../hooks/useNotification.js'
import { LinkPowerBi, conferirLink } from './comum'
import CampoDataset from './CampoDataset'

/** Novo dashboard: o tipo, o nome e o link do Power BI, conferido enquanto a pessoa cola. */
export default function NovoDashboard({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const navigate = useNavigate()
  const { notify } = useNotification()
  const [tipo, setTipo] = useState<'powerbi' | 'html'>('powerbi')
  const [nome, setNome] = useState('')
  const [link, setLink] = useState('')
  const [ordem, setOrdem] = useState('')
  const [dataset, setDataset] = useState('')
  const [datasetConferido, setDatasetConferido] = useState<string | null>(null)
  const [conferido, setConferido] = useState<LinkPowerBi | null>(null)
  const [conferindo, setConferindo] = useState(false)
  const [erroLink, setErroLink] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const nomeTocado = useRef(false)

  useEffect(() => {
    if (!aberto) return
    setTipo('powerbi'); setNome(''); setLink(''); setOrdem(''); setDataset(''); setConferido(null); setErroLink(null); setErro(null)
    nomeTocado.current = false
  }, [aberto])

  // Confere o link no Power BI um pouco depois de a pessoa parar de digitar.
  useEffect(() => {
    setConferido(null); setErroLink(null)
    if (!link.trim()) return
    const t = setTimeout(async () => {
      setConferindo(true)
      const r = await conferirLink(link)
      setConferindo(false)
      if (typeof r === 'string') { setErroLink(r); return }
      setConferido(r)
      if (!nomeTocado.current) setNome(r.nome)
      if (r.datasetCaminho) setDataset(r.datasetCaminho)
    }, 500)
    return () => clearTimeout(t)
  }, [link])

  const html = tipo === 'html'
  const tipoErrado = !!conferido && conferido.tipo !== tipo
  const ordemOk = ordem === '' || /^\d{1,4}$/.test(ordem)
  const pronto = nome.trim().length >= 3 && !!conferido && !tipoErrado && ordemOk && (html || !!datasetConferido)

  const criar = async () => {
    if (!conferido) return
    setSalvando(true); setErro(null)
    const res = await services.api.criarDashboard({
      name: nome.trim(), reportPath: conferido.caminho, type: tipo, active: true, datasetPath: html ? null : datasetConferido,
      autoRefreshTime: null, order: ordem === '' ? null : Number(ordem), enableFilters: false, enableNavigationBar: false
    })
    setSalvando(false)
    if (res.fail) { setErro(res.error || 'Não foi possível criar o dashboard.'); return }
    aoFechar()
    notify('Dashboard criado. Agora vincule aos ambientes.', { variant: 'success' })
    navigate(`/app/gestao/dashboards/${res.data.id}`)
  }

  return (
    <Janela aberto={aberto} titulo="Novo dashboard" sub="Entra no catálogo. Depois você vincula aos ambientes." botao="Criar dashboard"
            desabilitado={!pronto} salvando={salvando} erro={erro} aoConfirmar={criar} aoFechar={aoFechar}>
      <div className="nv-campo">
        <span>Tipo</span>
        <div className="ng-tipos">
          <button type="button" aria-pressed={!html} onClick={() => setTipo('powerbi')}><b>Power BI</b><small>O relatório do Power BI, embutido no portal.</small></button>
          <button type="button" aria-pressed={html} onClick={() => setTipo('html')}><b>Relatório HTML</b><small>Página feita sob medida com os dados do modelo. Abre no editor depois.</small></button>
        </div>
      </div>
      <label className="nv-campo">
        <span>Link do {html ? 'modelo semântico (dataset)' : 'relatório'} no Power BI</span>
        <input value={link} onChange={e => setLink(e.target.value)} placeholder={`Cole o endereço do ${html ? 'dataset' : 'relatório'} aberto no Power BI`} />
        {conferindo && <small>Conferindo no Power BI…</small>}
        {erroLink && <small style={{ color: '#c4362b', fontWeight: 700 }}>{erroLink}</small>}
        {conferido && !tipoErrado && <small style={{ color: '#2f8a46', fontWeight: 700 }}>Encontrado no Power BI: {conferido.nome}</small>}
        {tipoErrado && <small style={{ color: '#c4362b', fontWeight: 700 }}>Esse link é de {conferido!.tipo === 'html' ? 'um dataset' : 'um relatório'}. {html ? 'Para relatório HTML, use o link do dataset.' : 'Para Power BI, use o link do relatório.'}</small>}
      </label>
      {!html && (
        <CampoDataset classe="nv-campo" valor={dataset} aoMudar={setDataset} aoConferir={setDatasetConferido}
                      doRelatorio={conferido?.datasetCaminho ? { caminho: conferido.datasetCaminho, nome: conferido.datasetNome } : null} />
      )}
      <div className="nv-campos">
        <label className="nv-campo">
          <span>Nome no portal</span>
          <input value={nome} onChange={e => { nomeTocado.current = true; setNome(e.target.value) }} maxLength={100} placeholder="Ex.: Ruptura por loja" />
        </label>
        <label className="nv-campo">
          <span>Ordem no menu</span>
          <input value={ordem} onChange={e => setOrdem(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="Sem ordem: por nome" />
          <small>Menor aparece primeiro dentro do grupo.</small>
        </label>
      </div>
    </Janela>
  )
}
